import { Box, Card, CardContent, Chip, CircularProgress, GridLegacy, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { mindtimeSupportApi } from 'src/api/mindtime/supportApi';
import { MAIN_SIDEBAR_PATHS } from 'src/components/dashboard/mainSidebarNav';
import { getPageTitle } from 'src/config/projectConfig';
import useMounted from 'src/hooks/useMounted';
import DownloadsContent from 'src/mindtime/components/support/DownloadsContent';
import KnowledgeBaseBrowser from 'src/mindtime/components/support/knowledge-base/KnowledgeBaseBrowser';
import {
  buildKbArticleHref,
  isKbArticleLocation,
} from 'src/mindtime/components/support/knowledge-base/knowledgeBaseHelpers';
import type { ServerGroupCardData } from 'src/types/mindtime/support';

type SupportView = 'knowledge-base' | 'service-status' | 'downloads';

/* ─── Server group component ─── */
const ServerGroupCard = ({ group }: { group: ServerGroupCardData }) => {
  const allOnline = group.online === group.total;

  return (
    <Box
      sx={{
        flex: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #EDF2F7',
        borderRadius: 1,
        p: 1.5,
        boxSizing: 'border-box',
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pb={1.5}
        borderBottom="1px solid #EDF2F7"
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            component="img"
            src={group.flag}
            alt=""
            sx={{ width: 20, height: 14, objectFit: 'cover', borderRadius: '2px' }}
          />
          <Typography variant="body2" fontWeight={600} fontSize={13}>
            {group.label}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <FiberManualRecordIcon
            sx={{ fontSize: 10, color: allOnline ? '#15803D' : '#DCFCE7' }}
          />
          <Chip
            label={`All servers online (${String(group.online).padStart(2, '0')}/${String(group.total).padStart(2, '0')})`}
            size="small"
            sx={{
              fontSize: 11,
              height: 24,
              bgcolor: allOnline ? '#DCFCE7' : '#FFF5F5',
              color: allOnline ? '#15803D' : '#F56565',
              fontWeight: 500,
            }}
          />
        </Box>
      </Box>

      {group.servers.length > 0 && (
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, border: 'none', color: '#4A5568' }}>
                Server
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, border: 'none', color: '#4A5568' }}>
                Type
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12, border: 'none', color: '#4A5568' }}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {group.servers.map((srv) => (
              <TableRow key={srv.name}>
                <TableCell sx={{ fontSize: 12, border: 'none', py: 0.75 }}>
                  {srv.name}
                </TableCell>
                <TableCell sx={{ fontSize: 12, border: 'none', py: 0.75 }}>
                  {srv.type}
                </TableCell>
                <TableCell align="right" sx={{ border: 'none', py: 0.75 }}>
                  <Chip
                    icon={<FiberManualRecordIcon sx={{ fontSize: '8px !important' }} />}
                    label={srv.status}
                    size="small"
                    sx={{
                      fontSize: 11,
                      height: 22,
                      bgcolor: srv.status === 'Online' ? '#DCFCE7' : '#FFF5F5',
                      color: srv.status === 'Online' ? '#15803D' : '#F56565',
                      fontWeight: 500,
                      '& .MuiChip-icon': {
                        color: srv.status === 'Online' ? '#15803D' : '#F56565',
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

const ServiceStatusContent = () => {
  const isMounted = useMounted();
  const [serverGroups, setServerGroups] = useState<ServerGroupCardData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadServerStatus = async () => {
      try {
        const groups = await mindtimeSupportApi.getServerStatus();
        if (isMounted.current) {
          setServerGroups(groups);
          setError(null);
        }
      } catch {
        if (isMounted.current) {
          setError('Unable to load server status');
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };

    loadServerStatus();
  }, [isMounted]);

  return (
    <GridLegacy container spacing={3}>
      <GridLegacy item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              The current server status
            </Typography>
            {isLoading && (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} />
              </Box>
            )}
            {!isLoading && error && (
              <Typography variant="body2" color="error" fontSize={13}>
                {error}
              </Typography>
            )}
            {!isLoading && !error && (
              <GridLegacy container spacing={2} alignItems="stretch">
                {serverGroups.map((group) => (
                  <GridLegacy
                    item
                    xs={12}
                    md={6}
                    key={group.label}
                    sx={{ display: 'flex' }}
                  >
                    <ServerGroupCard group={group} />
                  </GridLegacy>
                ))}
              </GridLegacy>
            )}
          </CardContent>
        </Card>
      </GridLegacy>

      <GridLegacy item xs={12}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Interruptions &amp; Maintenance
            </Typography>
            <Typography variant="body2" fontSize={13} color="textSecondary" mb={2}>
              Every third Thursday of the month between 9:00 and 16:00, Mindtime Backup has the
              possibility to do maintenance. On these days there is a possibility the servers or MMS
              aren&apos;t available for a few moments.
            </Typography>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Upcoming Maintenance Days
            </Typography>
            <Box display="flex" gap={1}>
              <Chip
                label="20 August"
                variant="outlined"
                size="small"
                sx={{ fontSize: 12, color: '#38B2AC', borderColor: '#38B2AC' }}
              />
              <Chip
                label="17 September"
                variant="outlined"
                size="small"
                sx={{ fontSize: 12, color: '#38B2AC', borderColor: '#38B2AC' }}
              />
              <Chip
                label="15 October"
                variant="outlined"
                size="small"
                sx={{ fontSize: 12, color: '#38B2AC', borderColor: '#38B2AC' }}
              />
              <Chip
                label="19 November"
                variant="outlined"
                size="small"
                sx={{ fontSize: 12, color: '#38B2AC', borderColor: '#38B2AC' }}
              />
              <Chip
                label="17 December"
                variant="outlined"
                size="small"
                sx={{ fontSize: 12, color: '#38B2AC', borderColor: '#38B2AC' }}
              />
            </Box>
          </CardContent>
        </Card>
      </GridLegacy>
    </GridLegacy>
  );
};

const KnowledgeBaseContent = () => (
  <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
    <KnowledgeBaseBrowser />
  </Box>
);

const getSupportView = (pathname: string): SupportView => {
  if (pathname === MAIN_SIDEBAR_PATHS.SUPPORT_SERVICE_STATUS) return 'service-status';
  if (pathname === MAIN_SIDEBAR_PATHS.SUPPORT_DOWNLOADS) return 'downloads';
  return 'knowledge-base';
};

const SUPPORT_VIEW_TITLES: Record<SupportView, string> = {
  'knowledge-base': 'Knowledge Base',
  'service-status': 'Service Status',
  downloads: 'Downloads',
};

const Support = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Legacy `?tab=` URLs → dedicated Support routes
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (!tab) return;

    if (tab === 'service-status') {
      navigate(MAIN_SIDEBAR_PATHS.SUPPORT_SERVICE_STATUS, { replace: true });
      return;
    }
    if (tab === 'downloads') {
      navigate(MAIN_SIDEBAR_PATHS.SUPPORT_DOWNLOADS, { replace: true });
      return;
    }
    if (tab === 'knowledge-base') {
      navigate(buildKbArticleHref(), { replace: true });
    }
  }, [navigate, searchParams]);

  const view = useMemo(() => {
    if (isKbArticleLocation(location.pathname)) return 'knowledge-base';
    return getSupportView(location.pathname);
  }, [location.pathname]);

  const title = SUPPORT_VIEW_TITLES[view];
  const isKnowledgeBase = view === 'knowledge-base';

  return (
    <>
      <Helmet>
        <title>{getPageTitle(title)}</title>
      </Helmet>

      <Box
        p={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          ...(isKnowledgeBase
            ? {
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
              }
            : {}),
        }}
      >
        <Typography variant="h4" fontWeight={600} gutterBottom sx={{ flexShrink: 0 }}>
          {title}
        </Typography>

        <Box
          sx={
            isKnowledgeBase
              ? {
                  flex: 1,
                  minHeight: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }
              : undefined
          }
        >
          {view === 'knowledge-base' && <KnowledgeBaseContent />}
          {view === 'service-status' && <ServiceStatusContent />}
          {view === 'downloads' && <DownloadsContent />}
        </Box>
      </Box>
    </>
  );
};

export default Support;
